/**
 * Markdown anzeigen — in Ticket-Meldungen, Kommentaren und Stories (T-0160).
 *
 * Erwähnungen (`@Name`) und Ticketverweise (`T-0036`) bleiben hervorgehoben:
 * Jedes Textstück, das Markdown übrig lässt, läuft durch dieselbe Zerlegung wie
 * in `MentionText`. Rohes HTML rendert react-markdown nicht — ein Ticket kann
 * also kein Skript in die Seite schmuggeln.
 */

import { Children, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import { zeilenumbruecheErhalten } from '../../lib/markdown';
import { MentionInline, type ErwaehnungsOptionen } from './mention-textarea';

type Props = Partial<ErwaehnungsOptionen> & { text: string; className?: string };

export function MarkdownText({ text, className, personen = [], ...optionen }: Props) {
  const mit = (kinder: ReactNode) =>
    Children.map(kinder, (kind) =>
      typeof kind === 'string' ? <MentionInline text={kind} personen={personen} {...optionen} /> : kind,
    );

  const komponenten: Components = {
    p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{mit(children)}</p>,
    li: ({ children }) => <li className="my-0.5">{mit(children)}</li>,
    strong: ({ children }) => <strong className="font-semibold">{mit(children)}</strong>,
    em: ({ children }) => <em>{mit(children)}</em>,
    del: ({ children }) => <del>{mit(children)}</del>,
    td: ({ children }) => <td className="border px-2 py-1">{mit(children)}</td>,
    th: ({ children }) => <th className="border bg-muted px-2 py-1 text-left font-medium">{mit(children)}</th>,
    h1: ({ children }) => <h3 className="mb-1 mt-3 text-base font-semibold first:mt-0">{mit(children)}</h3>,
    h2: ({ children }) => <h4 className="mb-1 mt-3 text-sm font-semibold first:mt-0">{mit(children)}</h4>,
    h3: ({ children }) => <h5 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{mit(children)}</h5>,
    ul: ({ children }) => <ul className="my-1.5 list-disc pl-5">{children}</ul>,
    ol: ({ children }) => <ol className="my-1.5 list-decimal pl-5">{children}</ol>,
    blockquote: ({ children }) => (
      <blockquote className="my-1.5 border-l-2 pl-3 text-muted-foreground">{children}</blockquote>
    ),
    a: ({ children, href }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
        {children}
      </a>
    ),
    code: ({ children, className: sprache }) =>
      sprache ? (
        <code className={sprache}>{children}</code>
      ) : (
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
      ),
    pre: ({ children }) => (
      <pre className="my-1.5 overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">{children}</pre>
    ),
    table: ({ children }) => (
      <div className="my-1.5 overflow-x-auto">
        <table className="border-collapse text-xs">{children}</table>
      </div>
    ),
    hr: () => <hr className="my-3" />,
  };

  return (
    <div className={cn('break-words', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={komponenten}>
        {zeilenumbruecheErhalten(text)}
      </ReactMarkdown>
    </div>
  );
}
